import { useEffect, useState } from 'react';
import {
  Firestore,
  collection,
  query,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

export interface UserPresence {
  user_id: string;
  user_email: string;
  user_name?: string;
  last_seen: Date;
}

export function usePresence(
  db: Firestore,
  projectId: string | undefined,
  threadId: string | undefined,
  userId: string | undefined,
  userEmail: string | undefined
) {
  const [activeUsers, setActiveUsers] = useState<UserPresence[]>([]);
  const [loading, setLoading] = useState(true);

  // Update own presence
  useEffect(() => {
    if (!projectId || !threadId || !userId || !userEmail) return;

    const presenceRef = doc(db, 'projects', projectId, 'threads', threadId, 'presence', userId);

    // Set initial presence
    setDoc(presenceRef, {
      user_id: userId,
      user_email: userEmail,
      user_name: userEmail.split('@')[0],
      last_seen: serverTimestamp()
    });

    // Update presence every 30 seconds
    const intervalId = setInterval(() => {
      setDoc(presenceRef, {
        user_id: userId,
        user_email: userEmail,
        user_name: userEmail.split('@')[0],
        last_seen: serverTimestamp()
      });
    }, 30000);

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
      deleteDoc(presenceRef).catch(() => {});
    };
  }, [db, projectId, threadId, userId, userEmail]);

  // Listen to other users' presence
  useEffect(() => {
    if (!projectId || !threadId) {
      setActiveUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const presenceRef = collection(db, 'projects', projectId, 'threads', threadId, 'presence');
    const q = query(presenceRef);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const now = new Date();
        const users: UserPresence[] = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            const lastSeen = data.last_seen instanceof Timestamp ? data.last_seen.toDate() : new Date();
            
            return {
              user_id: data.user_id,
              user_email: data.user_email,
              user_name: data.user_name,
              last_seen: lastSeen
            };
          })
          .filter((user) => {
            // Only show users active in the last 2 minutes
            const diffMs = now.getTime() - user.last_seen.getTime();
            return diffMs < 2 * 60 * 1000;
          });
        
        setActiveUsers(users);
        setLoading(false);
      },
      (err) => {
        console.error('Presence error:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, projectId, threadId]);

  return { activeUsers, loading };
}
